// src/pages/interview/InterviewSetupPage.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CVUploader from "../../components/CVUploader";
import Loader from "../../components/Loader";
import { useInterviewContext } from "../../contexts/InterviewContext";
import { generateQuestions } from "../../services/interviewAssistantService";

const InterviewSetupPage = () => {
  const {
    cvData,
    questions,
    setQuestions,
    isLoading,
    setIsLoading,
    error,
    setError, // Renamed from setLocalError if that was a local state
    setInterviewStage,
    resetInterview,
  } = useInterviewContext();
  const navigate = useNavigate();
  const errorDisplayRef = useRef(null);
  const pageTitleRef = useRef(null);
  const [initialError, setInitialError] = useState(null); // For parameter check errors

  useEffect(() => {
    if (pageTitleRef.current) {
        pageTitleRef.current.focus();
    }
  },[]);

  useEffect(() => {
    if (error && errorDisplayRef.current) {
      errorDisplayRef.current.focus();
    } else if (initialError && errorDisplayRef.current){ // also focus if initialError is set
        errorDisplayRef.current.focus();
    }
  }, [error, initialError]);


  useEffect(() => {
    if (cvData.sessionId && questions && questions.length > 0) {
      setInterviewStage("active");
      navigate("/interview/active", { replace: true });
    } else if (
      cvData.sessionId &&
      cvData.extractedInfo && // Ensure extractedInfo is also present before generating questions
      !questions?.length &&
      !isLoading.questionGeneration && !error // Don't generate if there's already an error
    ) {
      const generateQs = async () => {
        setIsLoading((prev) => ({ ...prev, questionGeneration: true }));
        setError(null);
        try {
          const generatedQuestions = await generateQuestions(
            cvData.extractedInfo,
            cvData.jobDescription
          );
          if (
            generatedQuestions &&
            generatedQuestions.length > 0 &&
            generatedQuestions.every((q) => q.id !== undefined)
          ) {
            setQuestions(generatedQuestions);
            setInterviewStage("active");
            navigate("/interview/active");
          } else {
            setError(
              "Could not generate interview questions, or questions are missing IDs. Please try a different CV or job description."
            );
          }
        } catch (err) {
          console.error("Question Generation Error:", err);
          setError(`Failed to generate questions: ${err.message || "Unknown error"}`);
        } finally {
          setIsLoading((prev) => ({ ...prev, questionGeneration: false }));
        }
      };
      generateQs();
    }
  }, [
    cvData.jobDescription,
    cvData.extractedInfo,
    cvData.sessionId,
    questions,
    setQuestions,
    isLoading.questionGeneration,
    setIsLoading,
    setError,
    navigate,
    setInterviewStage,
    error // Added error to dependency array to prevent re-triggering generation if an error occurred
  ]);

  const handleGoHome = () => {
    resetInterview();
    navigate("/");
  };

  const handleStartOver = () => {
    resetInterview(); // This clears cvData, error, etc. from context
    setInitialError(null); // Clear initial parameter error as well
    // Stays on this page, CVUploader will re-appear
  };

  // Initial state: No CV data, not loading anything yet, and no initial parameter error
  if (
    !cvData.extractedInfo &&
    !cvData.sessionId && // Added check for sessionId as well for initial state
    !isLoading.cvAnalysis &&
    !isLoading.questionGeneration &&
    !initialError // Ensure no initial error before showing uploader
  ) {
    return (
      <main className="min-h-screen bg-slate-900 text-slate-100 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <button
              onClick={handleGoHome}
              className="inline-flex items-center text-sky-400 hover:text-sky-300 mb-4 group"
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5 mr-2 transition-transform duration-200 group-hover:-translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </button>
            <h1 ref={pageTitleRef} tabIndex={-1} className="text-3xl sm:text-4xl font-bold text-slate-50 mb-3 outline-none">
              Interview Practice Setup
            </h1>
            <p className="text-slate-300 text-lg">
              Upload your CV and optionally a job description to get started with
              personalized interview questions.
            </p>
          </div>
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 sm:p-8 border border-slate-700">
            <CVUploader /> {/* Assuming CVUploader is made theme-consistent separately */}
          </div>
          {error && ( // This error is from context, likely after CV upload attempt
            <div role="alert" aria-live="assertive" ref={errorDisplayRef} tabIndex={-1} className="mt-6 bg-red-700/20 border border-red-600/30 text-red-200 p-4 rounded-lg outline-none">
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
      </main>
    );
  }

  // Loading state for CV Analysis
  if (isLoading.cvAnalysis) {
    return (
      <main className="min-h-screen bg-slate-900 py-8 px-4 text-center flex flex-col justify-center items-center">
        <div role="status" aria-live="polite">
          <Loader message="Analyzing your CV and extracting relevant information..." />
        </div>
      </main>
    );
  }

  // CV Analysis complete, Question Generation in Progress (or an error occurred here)
  if (
    (cvData.extractedInfo && cvData.sessionId && isLoading.questionGeneration) ||
    (cvData.extractedInfo && cvData.sessionId && error) // Show this state if error during question gen
  ) {
    return (
      <main className="min-h-screen bg-slate-900 text-slate-100 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 sm:p-8 mb-6 border border-slate-700">
            <div className="bg-gradient-to-r from-green-700/30 to-sky-700/30 px-6 py-4 border-b border-slate-700 mb-6 rounded-t-lg -m-6 sm:-m-8 -mb-0 sm:-mb-0 p-6 sm:p-8">
              <div className="flex items-center">
                <svg
                  aria-hidden="true"
                  className="h-7 w-7 mr-3 text-green-400 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h2 ref={pageTitleRef} tabIndex={-1} className="text-xl font-semibold text-slate-100 outline-none">
                  CV Analysis Complete
                  {cvData.sessionId && <span className="text-sm text-slate-400 block sm:inline sm:ml-2">(Session ID: {cvData.sessionId.substring(0, 8)}...)</span>}
                </h2>
              </div>
            </div>
            <div className="pt-6"> {/* Added padding top because of negative margin trick for header */}
              {cvData.extractedInfo.skills && (
                <div>
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-2">
                    Identified Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {cvData.extractedInfo.skills.length > 0 ? (
                      cvData.extractedInfo.skills.slice(0, 10).map(
                        (skill, index) => (
                          <span
                            key={index}
                            className="bg-sky-700/50 text-sky-200 text-sm px-3 py-1 rounded-full"
                          >
                            {skill}
                          </span>
                        )
                      )
                    ) : (
                      <span className="text-slate-400 italic">
                        No specific skills extracted or CV is very general.
                      </span>
                    )}
                    {cvData.extractedInfo.skills.length > 10 && (
                      <span className="text-slate-400 text-sm">...and more.</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isLoading.questionGeneration && (
            <div className="text-center" role="status" aria-live="polite">
              <Loader message="Generating personalized interview questions..." />
            </div>
          )}

          {error && !isLoading.questionGeneration && ( // Show error if question generation failed and not loading anymore
            <div role="alert" aria-live="assertive" ref={errorDisplayRef} tabIndex={-1} className="mt-6 bg-red-700/20 border-l-4 border-red-500 text-red-200 p-4 rounded-md outline-none">
              <h3 className="font-bold">Question Generation Failed</h3>
              <p className="text-sm mt-1">{error}</p>
              <button
                onClick={handleStartOver}
                className="mt-3 px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-500"
              >
                Upload New CV/JD
              </button>
            </div>
          )}
        </div>
      </main>
    );
  }

  // Fallback for other unhandled error states or initial parameter errors
  if (error || initialError) {
    return (
      <main className="min-h-screen bg-slate-900 text-slate-100 py-8 px-4 flex flex-col justify-center items-center">
        <div role="alert" aria-live="assertive" ref={errorDisplayRef} tabIndex={-1} className="bg-slate-800 rounded-xl shadow-2xl p-6 sm:p-8 mb-6 text-center max-w-lg w-full border border-slate-700 outline-none">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Setup Error</h2>
          <p className="text-slate-300 mb-6">{error || initialError}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleStartOver}
              className="px-6 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-500 transition-colors"
            >
              Start Over
            </button>
            <button
              onClick={handleGoHome}
              className="px-6 py-3 bg-slate-700 text-slate-200 font-semibold rounded-lg hover:bg-slate-600 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </main>
    );
  }


  // Default loading state if none of the above conditions are met (e.g., preparing to navigate)
  return (
    <main className="min-h-screen bg-slate-900 py-8 px-4 text-center flex flex-col justify-center items-center">
       <div role="status" aria-live="polite">
        <Loader message="Preparing your interview session..." />
      </div>
    </main>
  );
};

export default InterviewSetupPage;
